<template>
    <form class="h-full w-full flex flex-col p-8" @submit.prevent="submitForm">
        <div class="grid grid-cols-2 gap-8">

            <div class="flex flex-col" :class="{ invalid: !email.isValid }">
                <label for="email">Email</label>
                <input type="email" id="email" v-model.trim="email.val" placeholder="" @blur="clearValidity('email')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!email.isValid" class="h-4 text-xs text-red-600">
                        {{ email.msg }}
                    </p>
                </transition>
            </div>

            <div class="flex flex-col" :class="{ invalid: !password.isValid }">
                <label for="password">Password</label>
                <input type="password" id="password" v-model.trim="password.val" placeholder=""
                    @blur="clearValidity('password')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!password.isValid" class="h-4 text-xs text-red-600">
                        {{ password.msg }}
                    </p>
                </transition>
            </div>

            <div class="flex flex-col" :class="{ invalid: !englishName.isValid }">
                <label for="english-name">English Name</label>
                <input type="text" id="english-name" v-model.trim="englishName.val" placeholder=""
                    @blur="clearValidity('englishName')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!englishName.isValid" class="h-4 text-xs text-red-600">
                        {{ englishName.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !arabicName.isValid }">
                <label for="arabic-name">Arabic Name</label>
                <input type="text" id="arabic-name" v-model.trim="arabicName.val" placeholder=""
                    @blur="clearValidity('arabicName')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!arabicName.isValid" class="h-4 text-xs text-red-600">
                        {{ arabicName.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !studentId.isValid }">
                <label for="student-id">Student ID</label>
                <input type="text" id="student-id" v-model.trim="studentId.val" placeholder=""
                    @blur="clearValidity('studentId')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!studentId.isValid" class="h-4 text-xs text-red-600">
                        {{ studentId.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !gender.isValid }">
                <label for="gender">Gender</label>
                <select class="ht-42" v-model.trim="gender.val" id="gender" @blur="clearValidity('gender')">
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                </select>
                <transition name="fade" mode="out-in">
                    <p v-if="!gender.isValid" class="h-4 text-xs text-red-600">
                        {{ gender.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !academicYear.isValid }">
                <label for="academic-year">Academic Year</label>
                <input type="text" id="academic-year" v-model.trim="academicYear.val" placeholder="eg:- 2015-2016"
                    @blur="clearValidity('academicYear')" @keypress="allowNumbersOnly" />
                <transition name="fade" mode="out-in">
                    <p v-if="!academicYear.isValid" class="h-4 text-xs text-red-600">
                        {{ academicYear.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !graduatedYear.isValid }">
                <label for="graduated-year">Graduated Year</label>
                <input type="number" min="2015" id="graduated-year" v-model.trim="graduatedYear.val"
                    placeholder="eg:- 2016" @blur="clearValidity('graduatedYear')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!graduatedYear.isValid" class="h-4 text-xs text-red-600">
                        {{ graduatedYear.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !graduatedDate.isValid }">
                <label for="graduated-date">Graduated Date</label>
                <input type="date" min="2015" id="graduated-date" v-model.trim="graduatedDate.val" placeholder=""
                    @blur="clearValidity('graduatedDate')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!graduatedDate.isValid" class="h-4 text-xs text-red-600">
                        {{ graduatedDate.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !emiratesId.isValid }">
                <label for="emirates-id">Emirates ID</label>
                <input type="text" id="emirates-id" maxlength="15" v-model.trim="emiratesId.val" placeholder=""
                    @blur="clearValidity('emiratesId')" @keypress="allowNumbersOnly" />
                <transition name="fade" mode="out-in">
                    <p v-if="!emiratesId.isValid" class="h-4 text-xs text-red-600">
                        {{ emiratesId.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !schoolId.isValid }">
                <label for="school-id">School ID</label>
                <input type="text" id="school-id" v-model.trim="schoolId.val" placeholder=""
                    @blur="clearValidity('schoolId')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!schoolId.isValid" class="h-4 text-xs text-red-600">
                        {{ schoolId.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !nationality.isValid }">
                <label for="nationality">Nationality</label>
                <country-list @on-select-country="onSelectCountry" @clear-validity="clearValidity('nationality')"
                    :error="!nationality.isValid" :place-holder="'Select Nationality'"></country-list>
                <transition name="fade" mode="out-in">
                    <p v-if="!nationality.isValid" class="h-4 text-xs text-red-600">
                        {{ nationality.msg }}
                    </p>
                </transition>
            </div>
        </div>

        <div class="w-full flex justify-center mt-8">
            <div v-if="isLoading"
                class="w-36 bg-gradient-to-r from-primary to-secondaryLight px-4 py-2 rounded-lg text-white flex items-center justify-center">
                <base-spinner-with-text class-list="h-6 w-6 text-white" text="Creating..."
                    text-class-list="text-white"></base-spinner-with-text>
            </div>
            <button v-else
                class="w-36 bg-gradient-to-r from-primary to-secondaryLight px-4 py-2 rounded-lg text-white">Create</button>
        </div>
    </form>
</template>

<script>
import gql from 'graphql-tag';
import CountryList from "../../../components/shared/CountryList.vue";
export default {
    name: "employment-users-form",
    emits: ["add-alumni-user"],
    components: {
        CountryList,
    },
    data() {
        return {
            email: {
                val: "",
                isValid: true,
                msg: "",
            },
            password: {
                val: "",
                isValid: true,
                msg: "",
            },
            englishName: {
                val: "",
                isValid: true,
                msg: "",
            },
            arabicName: {
                val: "",
                isValid: true,
                msg: "",
            },
            studentId: {
                val: "",
                isValid: true,
                msg: "",
            },
            gender: {
                val: "",
                isValid: true,
                msg: "",
            },
            academicYear: {
                val: "",
                isValid: true,
                msg: "",
            },
            graduatedYear: {
                val: "",
                isValid: true,
                msg: "",
            },
            graduatedDate: {
                val: "",
                isValid: true,
                msg: "",
            },
            emiratesId: {
                val: "",
                isValid: true,
                msg: "",
            },
            schoolId: {
                val: "",
                isValid: true,
                msg: "",
            },
            nationality: {
                val: "",
                isValid: true,
                msg: "",
            },
            formIsValid: true,
            isLoading: false,
            error: null,
            alumniUser: null,
        };
    },
    watch: {
        'academicYear.val'(newVal) {
            // Format the value as 'YYYY-YYYY'
            let value = newVal.replace(/\D/g, '');
            if (value.length > 4) {
                value = value.slice(0, 4) + '-' + value.slice(4);
            }
            this.academicYear.val = value.slice(0, 9);
        }
    },
    methods: {
        allowNumbersOnly(event) {
            const charCode = event.which || event.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                event.preventDefault();
            }
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateForm() {
            this.formIsValid = true;
            if (this.email.val === "") {
                this.email.isValid = false;
                this.email.msg = "Email must not be empty.";
                this.formIsValid = false;
            } else {
                const regexExp = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;
                if (!regexExp.test(this.email.val)) {
                    this.email.isValid = false;
                    this.email.msg = "The email field must be a valid email address.";
                    this.formIsValid = false;
                }
            }
            if (this.password.val === "") {
                this.password.isValid = false;
                this.password.msg = "Password must not be empty.";
                this.formIsValid = false;
            } else if (this.password.val.length < 6) {
                this.password.isValid = false;
                this.password.msg = "The password field must be at least 6 characters.";
                this.formIsValid = false;
            }
            if (this.englishName.val === "") {
                this.englishName.isValid = false;
                this.englishName.msg = "English Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.studentId.val === "") {
                this.studentId.isValid = false;
                this.studentId.msg = "Student ID must not be empty.";
                this.formIsValid = false;
            }
            if (this.gender.val === "") {
                this.gender.isValid = false;
                this.gender.msg = "Gender must not be empty.";
                this.formIsValid = false;
            }
            if (this.emiratesId.val === "") {
                this.emiratesId.isValid = false;
                this.emiratesId.msg = "Emirates ID must not be empty.";
                this.formIsValid = false;
            }
            if (this.schoolId.val === "") {
                this.schoolId.isValid = false;
                this.schoolId.msg = "School ID must not be empty.";
                this.formIsValid = false;
            }
            if (this.nationality.val === "") {
                this.nationality.isValid = false;
                this.nationality.msg = "Nationality must not be empty.";
                this.formIsValid = false;
            }
        },
        async submitForm() {
            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            const formData = {
                email: this.email.val,
                password: this.password.val,
                english_name: this.englishName.val,
                arabic_name: this.arabicName.val,
                student_id: this.studentId.val,
                gender: this.gender.val,
                academic_year: this.academicYear.val,
                graduated_year: this.graduatedYear.val,
                graduated_date: this.graduatedDate.val,
                emirates_id: this.emiratesId.val,
                school_id: this.schoolId.val,
                nationality: this.nationality.val,
            };

            const CREATE_ALUMNI_USER_MUTATION = gql`
                mutation createAlumniUser($input: CreateAlumniUserInput!) {
                    createAlumniUser(input: $input) {
                        alumni_details {
                            id
                            english_name
                            student_id
                            school_id
                            graduated_year
                        }
                    }
                }
            `;

            this.isLoading = true;

            try {

                await this.$apollo.mutate({
                    mutation: CREATE_ALUMNI_USER_MUTATION,
                    variables: {
                        input: formData
                    }
                }).then((data) => {
                    this.alumniUser = data.data.createAlumniUser.alumni_details;
                }).catch((error) => {
                    console.error(error)
                })

            } catch (error) {
                console.log(error);
                this.error = error.message || "Failed to authenticate, try later.";
            }

            if (this.error === null) {
                this.$emit('add-alumni-user');
            }

            this.isLoading = false;

        },
        onSelectCountry(country) {
            this.nationality.val = country;
        },
    },
}
</script>

<style lang="scss" scoped>
label {
    @apply mb-1 block text-sm;
}

input[type="text"],
input[type="email"],
input[type="number"],
input[type="password"],
input[type="date"],
select {
    @apply block w-full border rounded-lg py-2 px-2;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="date"]:focus,
select:focus {
    outline: none;
    border-color: #bc1fa6;
    box-shadow: 0 0 0 2px #bc1fa680;
}

.invalid label {
    @apply text-red-600;
}

.invalid input,
.invalid select {
    border: 1px solid red;
}

input[type="checkbox"] {
    @apply w-auto;
}

.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    height: 0px;
}

.ht-42 {
    height: 42px;
}
</style>